import React from 'react';

export const extractTemplateParameterName = (value?: string | null, fallbackIndex?: number): string => {
  const rawValue = value?.trim()

  if (!rawValue) {
    return fallbackIndex ? String(fallbackIndex) : ''
  }

  const normalizedValue = rawValue
    .replace(/^\{\{/, '')
    .replace(/\}\}$/, '')
    .trim()

  return normalizedValue || (fallbackIndex ? String(fallbackIndex) : '')
}

export const formatTemplateVariableLabel = (value?: string | null, fallbackIndex?: number): string => {
  const rawValue = value?.trim()

  if (!rawValue) {
    return fallbackIndex ? `Field ${fallbackIndex}` : 'Field'
  }

  const normalizedValue = extractTemplateParameterName(rawValue, fallbackIndex)

  if (/^\d+$/.test(normalizedValue)) {
    return `Field ${normalizedValue}`
  }

  const spacedValue = normalizedValue
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()

  if (!spacedValue) {
    return fallbackIndex ? `Field ${fallbackIndex}` : 'Field'
  }

  return spacedValue
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export const extractTemplateExamples = (components: any[]): Record<string, string> => {
  const examples: Record<string, string> = {}
  if (!components) return examples

  const bodyComponent = components.find((comp: any) => comp.type === 'BODY')
  if (bodyComponent?.example) {
    if (bodyComponent.example.body_text_named_params) {
      // Named parameters format
      bodyComponent.example.body_text_named_params.forEach((param: any) => {
        examples[param.param_name] = param.example
      })
    } else if (bodyComponent.example.body_text && bodyComponent.example.body_text[0]) {
      // Numbered parameters format
      bodyComponent.example.body_text[0].forEach((example: string, index: number) => {
        examples[String(index + 1)] = example
      })
    }
  }
  return examples
}

export const formatTextWithVariables = (
  text: string,
  exampleValues: string[] | Record<string, string> = []
) => {
  if (!text) return null;

  // Split text by {{name}} or {{number}} pattern
  const parts = text.split(/(\{\{[a-zA-Z0-9_-]+\}\})/);

  return parts.map((part, index) => {
    const match = part.match(/\{\{([a-zA-Z0-9_-]+)\}\}/);

    if (match) {
      const varKey = match[1];
      let exampleValue: string | undefined;

      if (Array.isArray(exampleValues)) {
        // If it's a number, use it as a 1-based index
        if (/^\d+$/.test(varKey)) {
          const varIndex = Number(varKey) - 1;
          exampleValue = exampleValues[varIndex];
        }
      } else {
        // If it's a record, use the key
        exampleValue = exampleValues[varKey];
      }

      if (exampleValue) {
        return React.createElement('span', { key: index }, exampleValue);
      }

      return React.createElement('span', { key: index }, `{{${varKey}}}`);
    }

    return React.createElement('span', { key: index }, part);
  });
};

export const detectVariablesFromBodyText = (bodyText: string) => {
  // Support both {{1}} and {{customer_name}} patterns
  const variablePattern = /\{\{([^}]+)\}\}/g
  const matches = Array.from(bodyText.matchAll(variablePattern))
  const uniqueVariables = [...new Set(matches.map(m => m[1]))] // Preserve order, remove duplicates

  return uniqueVariables.map((varName, idx) => ({
    index: idx + 1, // For backward compatibility
    parameter_name: varName,
    placeholder: `{{${varName}}}`,
    example: `Variable ${varName}`,
  }))
}
